export type ImportFormat = 'csv' | 'json';

export interface ParsedImport {
  format: ImportFormat;
  rowCount: number;
  columns: string[];
  source: string;
}

const REQUIRED_COLUMN_GROUPS = [
  ['artist', 'act', 'name', 'title', 'titlede'],
  ['stage_id', 'stageid', 'stage', 'bühne', 'buehne'],
  ['starts_at', 'startsat', 'start', 'beginn'],
  ['ends_at', 'endsat', 'end', 'ende'],
] as const;

export function detectDelimiter(source: string): ',' | ';' | '\t' {
  const firstRecord = source.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '';
  const counts = ([',', ';', '\t'] as const).map((delimiter) => ({
    delimiter,
    count: firstRecord.split(delimiter).length - 1,
  }));
  counts.sort((a, b) => b.count - a.count);
  return counts[0]?.delimiter ?? ',';
}

export function parseDelimitedRows(source: string, delimiter = detectDelimiter(source)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let quoted = false;
  const normalized = source.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  for (let index = 0; index < normalized.length; index += 1) {
    const character = normalized[index];
    const next = normalized[index + 1];

    if (character === '"') {
      if (quoted && next === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (!quoted && character === delimiter) {
      row.push(value.trim());
      value = '';
      continue;
    }

    if (!quoted && character === '\n') {
      row.push(value.trim());
      if (row.some((cell) => cell.length > 0)) rows.push(row);
      row = [];
      value = '';
      continue;
    }

    value += character;
  }

  if (quoted) throw new Error('Nicht geschlossenes Anführungszeichen in CSV-Datei.');
  row.push(value.trim());
  if (row.some((cell) => cell.length > 0)) rows.push(row);
  return rows;
}

export function inspectTimetableImport(source: string, filename = ''): ParsedImport {
  const trimmed = source.trim();
  if (trimmed.length === 0) throw new Error('Die Importdatei ist leer.');
  const format: ImportFormat =
    filename.toLowerCase().endsWith('.json') || trimmed.startsWith('{') || trimmed.startsWith('[')
      ? 'json'
      : 'csv';

  if (format === 'json') {
    const parsed = JSON.parse(trimmed) as unknown;
    const list = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === 'object' && 'timetable' in parsed
        ? (parsed as { timetable: unknown }).timetable
        : undefined;
    if (!Array.isArray(list)) {
      throw new Error('JSON muss ein Array oder ein Objekt mit „timetable“-Array enthalten.');
    }
    const columns = list[0] && typeof list[0] === 'object' ? Object.keys(list[0]) : [];
    return { format, rowCount: list.length, columns, source };
  }

  const rows = parseDelimitedRows(source);
  if (rows.length < 2) throw new Error('CSV muss eine Kopfzeile und mindestens einen Act enthalten.');
  const headers = (rows[0] ?? []).map((header) => header.trim().toLowerCase());
  const missing = REQUIRED_COLUMN_GROUPS.filter(
    (aliases) => !aliases.some((alias) => headers.includes(alias)),
  );
  if (missing.length > 0) {
    throw new Error(
      `Pflichtspalten fehlen: ${missing.map((aliases) => aliases.slice(0, 2).join('/')).join(', ')}.`,
    );
  }
  return { format, rowCount: rows.length - 1, columns: rows[0] ?? [], source };
}
